import {
  BatteryInfo,
  Device as CDevice,
  DeviceId,
  DeviceInfo,
  GetLanguageCodeResult,
  LanguageTag,
  OperatingSystem,
} from '@capacitor/device';

const NOT_INIT = 'NOT_INITIALIZED';
/**
 * This class wraps around @capacitor/device as some of the info won't change on runtime
 * and @capacitor/device's functions return annoying promises
 */
class DeviceCls {
  // DeviceId
  uuid: string = NOT_INIT;

  // DeviceInfo
  name?: string;
  model: string = NOT_INIT;
  platform: DeviceInfo['platform'] = 'web';
  operatingSystem: OperatingSystem = 'unknown';
  osVersion: string = NOT_INIT;
  manufacturer: string = NOT_INIT;
  isVirtual: boolean = true;
  webViewVersion: string = NOT_INIT;
  languageCode: string = NOT_INIT;

  async setup() {
    if (this.uuid !== NOT_INIT) return; // already setup

    this.uuid = (await CDevice.getId()).identifier;
    const info = await CDevice.getInfo();
    this.name = info.name;
    this.model = info.model;
    this.platform = info.platform;
    this.operatingSystem = info.operatingSystem;
    this.osVersion = info.osVersion;
    this.manufacturer = info.manufacturer;
    this.isVirtual = info.isVirtual;
    this.webViewVersion = info.webViewVersion;
    this.languageCode = (await CDevice.getLanguageCode()).value;
  }

  /**
   * Return an unique identifier for the device.
   *
   * @since 1.0.0
   */
  getId(): Promise<DeviceId> {
    return CDevice.getId();
  }
  /**
   * Return information about the underlying device/os/platform.
   *
   * @since 1.0.0
   */
  getInfo(): Promise<DeviceInfo> {
    return CDevice.getInfo();
  }
  /**
   * Return information about the battery.
   *
   * @since 1.0.0
   */
  getBatteryInfo(): Promise<BatteryInfo> {
    return CDevice.getBatteryInfo();
  }
  /**
   * Get the device's current language locale code.
   *
   * @since 1.0.0
   */
  getLanguageCode(): Promise<GetLanguageCodeResult> {
    return CDevice.getLanguageCode();
  }
  /**
   * Get the device's current language locale tag.
   *
   * @since 4.0.0
   */
  getLanguageTag(): Promise<LanguageTag> {
    return CDevice.getLanguageTag();
  }
}

export const Device = new DeviceCls();
